import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { PostTreatmentsBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/** Atomically records treatments — creates prescriptions, orders, and/or tasks as needed. */
export function useCreateTreatments() {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: PostTreatmentsBody) => {
      const response = await api.post('/treatments', { body })
      if (!response.success) throw new Error(response.error.message)
      return response.data
    },
    onSuccess: (_data, variables) => {
      toast.success('Treatment added')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product'] })
      queryClient.invalidateQueries({
        queryKey: ['patient-orders', variables.patientId],
      })
      queryClient.invalidateQueries({
        queryKey: ['patient-treatments', variables.patientId],
      })
      queryClient.invalidateQueries({
        queryKey: ['patient-timeline', variables.patientId],
      })
      if (variables.consultationId) {
        queryClient.invalidateQueries({
          queryKey: ['consultation', variables.consultationId],
        })
      }
      if (variables.items.some((item) => item.type === 'external_prescription_with_task')) {
        queryClient.invalidateQueries({ queryKey: ['tasks'] })
        queryClient.invalidateQueries({
          queryKey: ['patient-tasks', variables.patientId],
        })
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to add treatment')
    },
  })
}
